import { defineStore } from 'pinia'
import type { Vehicle, FilterOption } from '~/types'
import { useRouter, useRoute } from '#imports'
import { ref, computed, watch } from 'vue'
import { useNuxtApp } from '#imports'
// import { calculateWeeklyPayment } from '#layers/base/utils/finance' // Original path
import { calculateWeeklyPayment } from '../layers/base/utils/finance' // Using relative path

import { useVehiclesStore } from '~/stores/vehicles'

// Locally define ValueDisplayPair to match vehicle data structure
interface ValueDisplayPair {
  value?: string[];
  displayValue?: string[];
}

// --- Interfaces ---
export interface FrontPageFilters {
  condition: string[];
  make: string[];
  model: string[];
  body: string[];
  badge: string[];
  perweek?: { min: number; max: number; };
  search_keywords?: string;
}

interface FilterCounts {
  make: Record<string, number>;
  model: Record<string, number>;
  body: Record<string, number>;
  condition: Record<string, number>;
}

interface FrontPageState {
  // Vehicle counts
  vehicleCounts: {
    total: number;
    new: number;
    demo: number;
    used: number;
  };

  // Featured vehicles (cached subset)
  featuredVehicles: Vehicle[];

  // Popular makes and models (for quick selection)
  popularMakes: FilterOption[];
  popularModels: FilterOption[];

  // Front page specific filters
  frontPageFilters: FrontPageFilters;

  // Filter options specifically for the front page
  filterOptions: {
    makes: FilterOption[];
    models: FilterOption[];
    bodyTypes: FilterOption[];
    conditions: FilterOption[];
  };

  // Filter counts for each filter option
  filterCounts: FilterCounts;

  // **NEW: Store the calculated default per week range**
  defaultPerWeekRange: { min: number; max: number };

  // Loading states
  loading: boolean;
  error: string | null;
}

// --- Type Guard (Defined BEFORE use) ---
function isValueDisplayPair(value: any): value is ValueDisplayPair {
  return value && typeof value === 'object' && Array.isArray(value.value);
}

// --- Helper Functions (Defined BEFORE use) ---
function isValidFilterKey(key: string): key is keyof FrontPageFilters {
  return key === 'condition' || key === 'make' || key === 'model' || key === 'body' || key === 'perweek' || key === 'search_keywords' || key === 'badge';
}

function getFirstValue(prop: ValueDisplayPair | undefined): string | undefined {
  // Use the locally defined ValueDisplayPair structure
  return prop?.value?.[0];
}

const getYearAsString = (yearProp: ValueDisplayPair | number | undefined): string => {
  if (typeof yearProp === 'number') {
    return yearProp.toString();
  }
  // Use getFirstValue which now handles the array structure
  return getFirstValue(yearProp) || '';
};

const getPriceAsNumber = (priceProp: number | undefined): number => {
    // Assumes price is already number due to mapping in vehiclesStore
    return priceProp ?? 0;
};

// Define the helper function (or import if available elsewhere)
function isVehiclePriceProperty(price: any): price is { value: [number] } {
  // Basic check, adjust if the actual structure is different
  return price && typeof price === 'object' && 'value' in price && Array.isArray(price.value) && typeof price.value[0] === 'number';
}

// NEW Helper Function to filter vehicles while excluding specified filter types
function _getVehiclesFilteredBy(
  filters: FrontPageFilters,
  allVehicles: Vehicle[],
  vehiclesStore: any,
  excludeFilters?: Array<keyof FrontPageFilters>
): Vehicle[] {
  if (!Array.isArray(allVehicles)) {
    console.warn('[_getVehiclesFilteredBy] allVehicles is not an array!');
    return [];
  }

  const excluded = Array.isArray(excludeFilters) ? excludeFilters : (excludeFilters ? [excludeFilters] : []);
  // Compute default per-week range from vehiclesStore to avoid self-referencing this store during module init
  const priceRangeFilter = vehiclesStore.availableFilters.priceRange;
  const defaultMinPrice = priceRangeFilter?.min ?? 0;
  const defaultMaxPrice = priceRangeFilter?.max ?? 100000;
  const computedDefaultPerWeekRange = {
    min: calculateWeeklyPayment(defaultMinPrice),
    max: calculateWeeklyPayment(defaultMaxPrice)
  };

  const noFiltersConsideredActive = (() => {
      let hasActive = false;
      for (const key in filters) {
          if (excluded.includes(key as keyof FrontPageFilters)) continue;
          const filterKey = key as keyof FrontPageFilters;
          const value = filters[filterKey];
          if (filterKey === 'search_keywords') {
              if (value && String(value).trim()) hasActive = true;
          } else if (filterKey === 'perweek') {
               if (value && typeof value === 'object' && value !== null && 'min' in value && 'max' in value) {
                  if (value.min !== computedDefaultPerWeekRange.min || value.max !== computedDefaultPerWeekRange.max) {
                     hasActive = true;
                  }
               }
          } else if (Array.isArray(value) && value.length > 0) {
              hasActive = true;
          }
          if (hasActive) break;
      }
      return !hasActive;
  })();

  const filtered = allVehicles.filter((vehicle: Vehicle) => {
    // --- Make Check ---
    if (!excluded.includes('make') && filters.make.length > 0) {
      const vehicleMakeValue = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
      if (!filters.make.some(make => make.toLowerCase() === vehicleMakeValue)) {
        return false;
      }
    }

    // --- Model Check ---
    if (!excluded.includes('model') && filters.model.length > 0) {
       const vehicleMake = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
       const vehicleModel = String(getFirstValue(vehicle.model) ?? '').toLowerCase();
       const matches = filters.model.some(selectedModelFilter => {
          const lowerSelectedModelFilter = selectedModelFilter.toLowerCase();
          const filterParts = lowerSelectedModelFilter.split('_');
          // Handle cases where model might not have make prefix (fallback)
          if (filterParts.length < 2) return vehicleModel === lowerSelectedModelFilter;
          const filterMake = filterParts[0];
          const filterModelCode = filterParts.slice(1).join('_');
          // Match if make matches AND model code matches OR the full filter value matches the model code (legacy?)
          return (vehicleMake === filterMake && (vehicleModel === filterModelCode || vehicleModel === lowerSelectedModelFilter));
       });
       if (!matches) {
         return false;
       }
    }

    // --- Body Check ---
    if (!excluded.includes('body') && filters.body.length > 0) {
      const vehicleBodyValue = String(vehicle.body?.value?.[0] ?? '').toLowerCase();
      if (!filters.body.some(bodyType => bodyType.toLowerCase() === vehicleBodyValue)) {
        return false;
      }
    }

    // --- Badge Check ---
    if (!excluded.includes('badge') && filters.badge.length > 0) {
      // Get the model value for this vehicle
      const vehicleModel = String(getFirstValue(vehicle.model) ?? '');
      
      // Try to get badge value from the badge property
      let vehicleBadgeValues: string[] = [];
      if (vehicle.badge?.value?.[0]) {
        // Normalize badge value - convert to lowercase and handle special characters consistently
        const baseBadgeValue = String(vehicle.badge.value[0]).toLowerCase().trim();
        // Store both the scoped and unscoped badge value for matching
        vehicleBadgeValues.push(baseBadgeValue);
        vehicleBadgeValues.push(`${vehicleModel}:${baseBadgeValue}`);
      }
      
      // Also check badge in attributes
      if (Array.isArray(vehicle.attributes)) {
        const badgeAttribute = vehicle.attributes.find(attr => attr.Name === 'Badge' && attr.Value);
        if (badgeAttribute?.Value) {
          const baseBadgeValue = String(badgeAttribute.Value).toLowerCase().replace(/\s+/g, '-');
          // Store both the scoped and unscoped badge value for matching
          vehicleBadgeValues.push(baseBadgeValue);
          vehicleBadgeValues.push(`${vehicleModel}:${baseBadgeValue}`);
        }
      }
      
      // If no badges match, filter out this vehicle
      if (!vehicleBadgeValues.some(badgeValue => 
        filters.badge.some(selectedBadge => {
          // Check both exact match and model-scoped match
          const selectedBadgeParts = selectedBadge.split(':');
          const selectedBadgeValue = selectedBadgeParts.length > 1 ? selectedBadgeParts[1] : selectedBadge;
          
          // Normalize comparison by trimming and forcing lowercase
          const normalizedBadgeValue = badgeValue.toLowerCase().trim();
          const normalizedSelectedBadge = selectedBadge.toLowerCase().trim();
          const normalizedSelectedBadgeValue = selectedBadgeValue?.toLowerCase().trim() || '';
          
          // Also check if the badge has a model scope, and if so, validate against vehicle model
          if (selectedBadgeParts.length > 1) {
            const selectedModel = selectedBadgeParts[0];
            // If badge has model scope, make sure model matches too
            const modelMatches = selectedModel && (vehicleModel.includes(selectedModel) || selectedModel.includes(vehicleModel));
            return normalizedBadgeValue === normalizedSelectedBadge || 
                  (normalizedBadgeValue === normalizedSelectedBadgeValue && modelMatches);
          }
          // For badges without model scope, just match the badge value
          return normalizedBadgeValue === normalizedSelectedBadge || 
                 normalizedBadgeValue === normalizedSelectedBadgeValue;
        })
      )) {
        return false;
      }
    }

    // --- Condition Check ---
    if (!excluded.includes('condition') && filters.condition.length > 0) {
      const vehicleCondition = String(getFirstValue(vehicle.condition) ?? '').toLowerCase();
      if (!filters.condition.some(c => c.toLowerCase() === vehicleCondition)) {
        return false;
      }
    }

    // --- Price Check (REVISED) ---
    if (!excluded.includes('perweek') && filters.perweek) {
        let vehiclePerWeek = Number(vehicle.perweek ?? 0);
        // Calculate perweek from price if necessary
        if (vehiclePerWeek <= 0 && vehicle.price) {
            let price = 0;
            if (typeof vehicle.price === 'number') {
                price = vehicle.price;
            } else if (isVehiclePriceProperty(vehicle.price)) { // Make sure isVehiclePriceProperty is in scope
                price = Number(vehicle.price.value[0]);
            }
            vehiclePerWeek = calculateWeeklyPayment(price); // Make sure calculateWeeklyPayment is in scope
        }

        // Always apply the filter based on the min/max values in filters.perweek
        if (vehiclePerWeek < filters.perweek.min || vehiclePerWeek > filters.perweek.max) {
            return false; // Vehicle is outside the specified perweek range
        }
    }

    // --- Keyword Check ---
    if (!excluded.includes('search_keywords') && filters.search_keywords) {
        const searchTerm = filters.search_keywords.toLowerCase().trim();
        if (searchTerm) {
            // Reusing the comprehensive search logic from navSearchFilteredCount
            const vehicleTitle = (vehicle.title ?? '').toLowerCase();
            const make = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
            const model = String(getFirstValue(vehicle.model) ?? '').toLowerCase();
            const badge = String(getFirstValue(vehicle.badge) ?? '').toLowerCase();
            const condition = String(getFirstValue(vehicle.condition) ?? '').toLowerCase();
            const bodyType = String(getFirstValue(vehicle.body) ?? '').toLowerCase();
            const transmissionValue = String(getFirstValue(vehicle.transmission) ?? '').toLowerCase();
            const fuelValue = String(getFirstValue(vehicle.fuel) ?? '').toLowerCase();
            const engineValue = String(getFirstValue(vehicle.engine) ?? '').toLowerCase();
            const colourValue = String(getFirstValue(vehicle.colour) ?? '').toLowerCase();
            const genericColour = String(vehicle.genericolour ?? '').toLowerCase();
            const description = String(vehicle.description ?? '').toLowerCase();
            const comments = String(vehicle.Comments ?? '').toLowerCase();
            const specification = String(vehicle.specification ?? '').toLowerCase();
            let attributesString = '';
            if (Array.isArray(vehicle.attributes)) {
                attributesString = vehicle.attributes
                .map(attr => `${attr.Name || ''} ${attr.Value || ''}`)
                .join(' ')
                .toLowerCase();
            }
            const year = getYearAsString(vehicle.year);
            const searchableText = [
                vehicleTitle, make, model, badge, year, condition, bodyType,
                transmissionValue, fuelValue, engineValue,
                colourValue, genericColour,
                description, attributesString, comments, specification
            ].join(' ');
            const searchWords = searchTerm.split(/\s+/);
            const someWordsMatch = searchWords.some(word => searchableText.includes(word));
            if (!someWordsMatch) {
               return false;
            }
        }
    }

    return true; // Vehicle passes applicable filters
  });
  return filtered;
}


// --- Store Definition ---
export const useFrontPageStore = defineStore('frontPage', {
  state: (): FrontPageState => ({
    vehicleCounts: {
      total: 0,
      new: 0,
      demo: 0,
      used: 0
    },
    featuredVehicles: [],
    popularMakes: [],
    popularModels: [],
    frontPageFilters: {
      condition: [],
      make: [],
      model: [],
      body: [],
      badge: [],
      perweek: {
        min: 0,
        max: 1000
      },
      search_keywords: ''
    },
    filterOptions: {
      makes: [],
      models: [],
      bodyTypes: [],
      conditions: []
    },
    filterCounts: {
      make: {},
      model: {},
      body: {},
      condition: {}
    },
    // **NEW: Initialize default per week range**
    defaultPerWeekRange: { min: 0, max: 1000 }, // Initial placeholder, will be calculated
    loading: false,
    error: null
  }),

  getters: {
    // Get filtered vehicle count based on current front page filters
    filteredVehicleCount(): number {
      const vehiclesStore = useVehiclesStore();

      // If no filters are active, return total count
      if (this.hasNoActiveFilters) {
        return this.vehicleCounts.total;
      }

      // Count vehicles that match the front page filters
      // Cast vehiclesStore.vehicles to Vehicle[] to satisfy the filter callback type
      return (vehiclesStore.vehicles as Vehicle[]).filter((vehicle: Vehicle) => {
        // Check make filter
        if (this.frontPageFilters.make.length > 0) {
          // Use safer access with nullish coalescing
          const vehicleMakeValue = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
          // Check if vehicle's make (lowercase) is included in the selected makes (assume selected makes are lowercase or compare case-insensitively)
          if (!this.frontPageFilters.make.some(make => make.toLowerCase() === vehicleMakeValue)) {
            return false;
          }
        }

        // Check model filter
        if (this.frontPageFilters.model.length > 0) {
           // Use safer access with nullish coalescing
          const vehicleMake = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
          const vehicleModelCode = String(getFirstValue(vehicle.model) ?? '');
          const vehicleCombinedModel = `${vehicleMake}_${vehicleModelCode}`.toLowerCase();
          // Check against combined value first, then just model code as fallback
          // Assume selected model filter value is the canonical 'make_modelcode'
          if (!this.frontPageFilters.model.some(selectedModel =>
              selectedModel.toLowerCase() === vehicleCombinedModel
          )) {
            return false;
          }
        }

        // Check body filter
        // Use safer access with optional chaining and nullish coalescing
        if (this.frontPageFilters.body.length > 0 && !this.frontPageFilters.body.includes(vehicle.body?.value?.[0] ?? '')) {
          return false;
        }

        // Check condition filter
        if (this.frontPageFilters.condition.length > 0) {
          // Use safer access with nullish coalescing
          const vehicleCondition = String(getFirstValue(vehicle.condition) ?? '').toLowerCase();
          if (!this.frontPageFilters.condition.some(c => c.toLowerCase() === vehicleCondition)) {
            return false;
          }
        }

        // Check price range
        if (this.frontPageFilters.perweek) {
           // Use safer access with nullish coalescing
          const price = isVehiclePriceProperty(vehicle.price) ? vehicle.price.value[0] : undefined;
          // Check if price is defined before comparing
          if (price === undefined || price < this.frontPageFilters.perweek.min || price > this.frontPageFilters.perweek.max) {
            return false;
          }
        }

        // Check search keywords
        if (this.frontPageFilters.search_keywords) {
            const searchTerm = this.frontPageFilters.search_keywords.toLowerCase().trim();
            if (searchTerm) { // Only filter if search term is not empty
              const vehicleTitle = (vehicle.title ?? '').toLowerCase(); // Safer access
              const make = String(getFirstValue(vehicle.make) ?? '').toLowerCase(); // Safer access
              const model = String(getFirstValue(vehicle.model) ?? '').toLowerCase(); // Safer access
              const badge = String(getFirstValue(vehicle.badge) ?? '').toLowerCase(); // Safer access
              const condition = String(getFirstValue(vehicle.condition) ?? '').toLowerCase(); // Safer access
              const bodyType = String(getFirstValue(vehicle.body) ?? '').toLowerCase(); // Safer access
              // Helper to get year as string, handling different types
              const year = getYearAsString(vehicle.year); // Use the helper

              // Combine searchable fields
              const searchableText = `${vehicleTitle} ${make} ${model} ${badge} ${year} ${condition} ${bodyType}`;

              // Split search term into individual words for better multi-word matching
              const searchWords = searchTerm.split(/\s+/);

              // Check if all search words are included in the searchable text
              const allWordsMatch = searchWords.every(word => searchableText.includes(word));

              if (!allWordsMatch) {
                  return false;
              }
            }
        }

        return true;
      }).length;
    },

    // NEW: Dedicated filtered vehicle count for NavSearchFilters
    navSearchFilteredCount(): number {
      // console.log(`[Getter navSearchFilteredCount] Evaluating... Filters:`, JSON.stringify(this.frontPageFilters)); // Removed log
      const vehiclesStore = useVehiclesStore();
      let vehiclesToFilter = vehiclesStore.vehicles;
      if (!Array.isArray(vehiclesToFilter)) {
        // console.log(`[Getter navSearchFilteredCount] Returning -4 (vehiclesToFilter not array)`); // Removed log
        return -4;
      }

      let noActiveFilters;
      try {
        noActiveFilters = this.hasNoActiveFilters;
      } catch (e) {
        return -2;
      }

      if (noActiveFilters) {
        return this.vehicleCounts.total;
      }

      try {
        const filteredVehicles = vehiclesToFilter.filter((vehicle: Vehicle) => {
          // --- Re-add variable declarations ---
          const vehicleId = vehicle.id || vehicle.stockid || 'UnknownID';

          // --- Make Check ---
          if (this.frontPageFilters.make.length > 0) {
            const vehicleMakeValue = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
            if (!this.frontPageFilters.make.some(make => make.toLowerCase() === vehicleMakeValue)) {
              return false;
            }
          }

          // --- Model Check ---
          if (this.frontPageFilters.model.length > 0) {
            const vehicleMake = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
            const vehicleModel = String(getFirstValue(vehicle.model) ?? '').toLowerCase();
            const matches = this.frontPageFilters.model.some(selectedModelFilter => {
                const lowerSelectedModelFilter = selectedModelFilter.toLowerCase();
                const filterParts = lowerSelectedModelFilter.split('_');
                if (filterParts.length < 2) return vehicleModel === lowerSelectedModelFilter;
                const filterMake = filterParts[0];
                const filterModelCode = filterParts.slice(1).join('_');
                return (vehicleMake === filterMake && (vehicleModel === filterModelCode || vehicleModel === lowerSelectedModelFilter));
            });
            if (!matches) {
              return false;
            }
          }

          // --- Body Check ---
          if (this.frontPageFilters.body.length > 0) {
            const vehicleBodyValue = String(vehicle.body?.value?.[0] ?? '').toLowerCase();
            if (!this.frontPageFilters.body.some(bodyType => bodyType.toLowerCase() === vehicleBodyValue)) {
              return false;
            }
          }

          // --- Badge Check ---
          if (this.frontPageFilters.badge.length > 0) {
            // Get the model value for this vehicle
            const vehicleModel = String(getFirstValue(vehicle.model) ?? '');
            
            // Try to get badge value from the badge property
            let vehicleBadgeValues: string[] = [];
            if (vehicle.badge?.value?.[0]) {
              const baseBadgeValue = String(vehicle.badge.value[0]).toLowerCase();
              // Store both the scoped and unscoped badge value for matching
              vehicleBadgeValues.push(baseBadgeValue);
              vehicleBadgeValues.push(`${vehicleModel}:${baseBadgeValue}`);
            }
            
            // Also check badge in attributes
            if (Array.isArray(vehicle.attributes)) {
              const badgeAttribute = vehicle.attributes.find(attr => attr.Name === 'Badge' && attr.Value);
              if (badgeAttribute?.Value) {
                const baseBadgeValue = String(badgeAttribute.Value).toLowerCase().replace(/\s+/g, '-');
                // Store both the scoped and unscoped badge value for matching
                vehicleBadgeValues.push(baseBadgeValue);
                vehicleBadgeValues.push(`${vehicleModel}:${baseBadgeValue}`);
              }
            }
            
            // If no badges match, filter out this vehicle
            if (!vehicleBadgeValues.some(badgeValue => 
              this.frontPageFilters.badge.some(selectedBadge => {
                // Check both exact match and model-scoped match
                const selectedBadgeParts = selectedBadge.split(':');
                const selectedBadgeValue = selectedBadgeParts.length > 1 ? selectedBadgeParts[1] : selectedBadge;
                
                // Normalize comparison by trimming and forcing lowercase
                const normalizedBadgeValue = badgeValue.toLowerCase().trim();
                const normalizedSelectedBadge = selectedBadge.toLowerCase().trim();
                const normalizedSelectedBadgeValue = selectedBadgeValue?.toLowerCase().trim() || '';
                
                // Also check if the badge has a model scope, and if so, validate against vehicle model
                if (selectedBadgeParts.length > 1) {
                  const selectedModel = selectedBadgeParts[0];
                  // If badge has model scope, make sure model matches too
                  const modelMatches = selectedModel && (vehicleModel.includes(selectedModel) || selectedModel.includes(vehicleModel));
                  return normalizedBadgeValue === normalizedSelectedBadge || 
                        (normalizedBadgeValue === normalizedSelectedBadgeValue && modelMatches);
                }
                // For badges without model scope, just match the badge value
                return normalizedBadgeValue === normalizedSelectedBadge || 
                       normalizedBadgeValue === normalizedSelectedBadgeValue;
              })
            )) {
              return false;
            }
          }

          // --- Condition Check ---
          if (this.frontPageFilters.condition.length > 0) {
            const vehicleCondition = String(getFirstValue(vehicle.condition) ?? '').toLowerCase();
            if (!this.frontPageFilters.condition.some(c => c.toLowerCase() === vehicleCondition)) {
              return false;
            }
          }

          // --- Price Check (FIXED: Use availableFilters) ---
          if (this.frontPageFilters.perweek) {
             const priceRangeFilter = vehiclesStore.availableFilters.priceRange;
             const defaultMin = priceRangeFilter?.min ?? 0;
             const defaultMax = priceRangeFilter?.max ?? 100000;

             // Calculate default perweek range based on default price range
             // This calculation might need adjustment based on your finance utils
             const defaultPerweekMin = calculateWeeklyPayment(defaultMin);
             const defaultPerweekMax = calculateWeeklyPayment(defaultMax);

            let isDefaultPriceRange = this.frontPageFilters.perweek.min === defaultPerweekMin &&
                                      this.frontPageFilters.perweek.max === defaultPerweekMax;

            if (!isDefaultPriceRange) {
              // First try to use the perweek value if it's already calculated
              let perweek = Number(vehicle.perweek ?? 0);

              // If perweek is not available or invalid, calculate it from price
              if (perweek <= 0 && vehicle.price) {
                let price = 0;

                if (typeof vehicle.price === 'number') {
                  price = vehicle.price;
                } else if (isVehiclePriceProperty(vehicle.price)) {
                  price = Number(vehicle.price.value[0]);
                }

                perweek = calculateWeeklyPayment(price);
              }

              if (perweek < this.frontPageFilters.perweek.min || perweek > this.frontPageFilters.perweek.max) {
                return false;
              }
            }
          }

          // --- Keyword Check ---
          if (this.frontPageFilters.search_keywords) {
              const searchTerm = this.frontPageFilters.search_keywords.toLowerCase().trim();
              if (searchTerm) {
                // --- Expand searchable fields ---
                const vehicleTitle = (vehicle.title ?? '').toLowerCase();
                const make = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
                const model = String(getFirstValue(vehicle.model) ?? '').toLowerCase();
                const badge = String(getFirstValue(vehicle.badge) ?? '').toLowerCase();
                const condition = String(getFirstValue(vehicle.condition) ?? '').toLowerCase();
                const bodyType = String(getFirstValue(vehicle.body) ?? '').toLowerCase();
                const transmissionValue = String(getFirstValue(vehicle.transmission) ?? '').toLowerCase();
                const fuelValue = String(getFirstValue(vehicle.fuel) ?? '').toLowerCase();
                const engineValue = String(getFirstValue(vehicle.engine) ?? '').toLowerCase();
                const colourValue = String(getFirstValue(vehicle.colour) ?? '').toLowerCase();
                const genericColour = String(vehicle.genericolour ?? '').toLowerCase();
                const description = String(vehicle.description ?? '').toLowerCase();
                const comments = String(vehicle.Comments ?? '').toLowerCase();
                const specification = String(vehicle.specification ?? '').toLowerCase();

                // Search within attributes array
                let attributesString = '';
                if (Array.isArray(vehicle.attributes)) {
                  attributesString = vehicle.attributes
                    .map(attr => `${attr.Name || ''} ${attr.Value || ''}`)
                    .join(' ')
                    .toLowerCase();
                }

                const year = getYearAsString(vehicle.year);

                // Combine searchable fields (using corrected fields and adding description/attributes/comments/spec)
                const searchableText = [
                  vehicleTitle, make, model, badge, year, condition, bodyType,
                  transmissionValue, fuelValue, engineValue,
                  colourValue, genericColour,
                  description, attributesString, comments, specification
                ].join(' ');

                // Split search term into individual words for better multi-word matching
                const searchWords = searchTerm.split(/\s+/);

                // Check if AT LEAST ONE search word is included in the searchable text
                const someWordsMatch = searchWords.some(word => searchableText.includes(word));

                if (!someWordsMatch) {
                  // console.log(`[navSearchFilteredCount] Vehicle ${vehicleId} failed KEYWORD filter (SOME). Search: '${searchTerm}', Words: [${searchWords.join(', ')}], Text: '${searchableText.substring(0, 100)}...'`); // Debug logging
                  return false;
                }
              }
          }

          return true; // Vehicle passes all active filters
        });
        const count = filteredVehicles.length;
        // console.log(`[Getter navSearchFilteredCount] Finished. Returning count: ${count}`); // Removed log
        return count;
      } catch (e) {
        // console.error(`[Getter navSearchFilteredCount] Error during filtering:`, e); // Removed log
        return -3; // Return error code
      }
    },

    // NEW Getter: Returns the array of vehicles matching current filters
    currentlyFilteredVehicles(): Vehicle[] {
      const vehiclesStore = useVehiclesStore();
      let vehiclesToFilter = vehiclesStore.vehicles;
      if (!Array.isArray(vehiclesToFilter)) {
        return []; // Return empty array if base data isn't ready
      }

      // Reuse the hasNoActiveFilters check
      if (this.hasNoActiveFilters) {
        return vehiclesToFilter; // Return all vehicles if no filters are active
      }

      // Reuse the filtering logic from navSearchFilteredCount
      try {
        const filteredVehicles = vehiclesToFilter.filter((vehicle: Vehicle) => {
          // --- Re-add variable declarations ---
          const vehicleId = vehicle.id || vehicle.stockid || 'UnknownID';

          // --- Make Check ---
          if (this.frontPageFilters.make.length > 0) {
            const vehicleMakeValue = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
            if (!this.frontPageFilters.make.some(make => make.toLowerCase() === vehicleMakeValue)) {
              return false;
            }
          }

          // --- Model Check ---
          if (this.frontPageFilters.model.length > 0) {
            const vehicleMake = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
            const vehicleModel = String(getFirstValue(vehicle.model) ?? '').toLowerCase();
            const matches = this.frontPageFilters.model.some(selectedModelFilter => {
                const lowerSelectedModelFilter = selectedModelFilter.toLowerCase();
                const filterParts = lowerSelectedModelFilter.split('_');
                if (filterParts.length < 2) return vehicleModel === lowerSelectedModelFilter;
                const filterMake = filterParts[0];
                const filterModelCode = filterParts.slice(1).join('_');
                return (vehicleMake === filterMake && (vehicleModel === filterModelCode || vehicleModel === lowerSelectedModelFilter));
            });
            if (!matches) {
              return false;
            }
          }

          // --- Body Check ---
          if (this.frontPageFilters.body.length > 0) {
            const vehicleBodyValue = String(vehicle.body?.value?.[0] ?? '').toLowerCase();
            if (!this.frontPageFilters.body.some(bodyType => bodyType.toLowerCase() === vehicleBodyValue)) {
              return false;
            }
          }

          // --- Badge Check ---
          if (this.frontPageFilters.badge.length > 0) {
            // Get the model value for this vehicle
            const vehicleModel = String(getFirstValue(vehicle.model) ?? '');
            
            // Try to get badge value from the badge property
            let vehicleBadgeValues: string[] = [];
            if (vehicle.badge?.value?.[0]) {
              const baseBadgeValue = String(vehicle.badge.value[0]).toLowerCase();
              // Store both the scoped and unscoped badge value for matching
              vehicleBadgeValues.push(baseBadgeValue);
              vehicleBadgeValues.push(`${vehicleModel}:${baseBadgeValue}`);
            }
            
            // Also check badge in attributes
            if (Array.isArray(vehicle.attributes)) {
              const badgeAttribute = vehicle.attributes.find(attr => attr.Name === 'Badge' && attr.Value);
              if (badgeAttribute?.Value) {
                const baseBadgeValue = String(badgeAttribute.Value).toLowerCase().replace(/\s+/g, '-');
                // Store both the scoped and unscoped badge value for matching
                vehicleBadgeValues.push(baseBadgeValue);
                vehicleBadgeValues.push(`${vehicleModel}:${baseBadgeValue}`);
              }
            }
            
            // If no badges match, filter out this vehicle
            if (!vehicleBadgeValues.some(badgeValue => 
              this.frontPageFilters.badge.some(selectedBadge => {
                // Check both exact match and model-scoped match
                const selectedBadgeParts = selectedBadge.split(':');
                const selectedBadgeValue = selectedBadgeParts.length > 1 ? selectedBadgeParts[1] : selectedBadge;
                
                // Normalize comparison by trimming and forcing lowercase
                const normalizedBadgeValue = badgeValue.toLowerCase().trim();
                const normalizedSelectedBadge = selectedBadge.toLowerCase().trim();
                const normalizedSelectedBadgeValue = selectedBadgeValue?.toLowerCase().trim() || '';
                
                // Also check if the badge has a model scope, and if so, validate against vehicle model
                if (selectedBadgeParts.length > 1) {
                  const selectedModel = selectedBadgeParts[0];
                  // If badge has model scope, make sure model matches too
                  const modelMatches = selectedModel && (vehicleModel.includes(selectedModel) || selectedModel.includes(vehicleModel));
                  return normalizedBadgeValue === normalizedSelectedBadge || 
                        (normalizedBadgeValue === normalizedSelectedBadgeValue && modelMatches);
                }
                // For badges without model scope, just match the badge value
                return normalizedBadgeValue === normalizedSelectedBadge || 
                       normalizedBadgeValue === normalizedSelectedBadgeValue;
              })
            )) {
              return false;
            }
          }

          // --- Condition Check ---
          if (this.frontPageFilters.condition.length > 0) {
            const vehicleCondition = String(getFirstValue(vehicle.condition) ?? '').toLowerCase();
            if (!this.frontPageFilters.condition.some(c => c.toLowerCase() === vehicleCondition)) {
              return false;
            }
          }

          // --- Price Check (FIXED: Use availableFilters) ---
          if (this.frontPageFilters.perweek) {
             const priceRangeFilter = vehiclesStore.availableFilters.priceRange;
             const defaultMin = priceRangeFilter?.min ?? 0;
             const defaultMax = priceRangeFilter?.max ?? 100000;

             // Calculate default perweek range based on default price range
             const defaultPerweekMin = calculateWeeklyPayment(defaultMin);
             const defaultPerweekMax = calculateWeeklyPayment(defaultMax);

            let isDefaultPriceRange = this.frontPageFilters.perweek.min === defaultPerweekMin &&
                                      this.frontPageFilters.perweek.max === defaultPerweekMax;

            if (!isDefaultPriceRange) {
              // First try to use the perweek value if it's already calculated
              let perweek = Number(vehicle.perweek ?? 0);

              // If perweek is not available or invalid, calculate it from price
              if (perweek <= 0 && vehicle.price) {
                let price = 0;

                if (typeof vehicle.price === 'number') {
                  price = vehicle.price;
                } else if (isVehiclePriceProperty(vehicle.price)) {
                  price = Number(vehicle.price.value[0]);
                }

                perweek = calculateWeeklyPayment(price);
              }

              if (perweek < this.frontPageFilters.perweek.min || perweek > this.frontPageFilters.perweek.max) {
                return false;
              }
            }
          }

          // --- Keyword Check ---
          if (this.frontPageFilters.search_keywords) {
              const searchTerm = this.frontPageFilters.search_keywords.toLowerCase().trim();
              if (searchTerm) {
                // --- Expand searchable fields ---
                const vehicleTitle = (vehicle.title ?? '').toLowerCase();
                const make = String(getFirstValue(vehicle.make) ?? '').toLowerCase();
                const model = String(getFirstValue(vehicle.model) ?? '').toLowerCase();
                const badge = String(getFirstValue(vehicle.badge) ?? '').toLowerCase();
                const condition = String(getFirstValue(vehicle.condition) ?? '').toLowerCase();
                const bodyType = String(getFirstValue(vehicle.body) ?? '').toLowerCase();
                const transmissionValue = String(getFirstValue(vehicle.transmission) ?? '').toLowerCase();
                const fuelValue = String(getFirstValue(vehicle.fuel) ?? '').toLowerCase();
                const engineValue = String(getFirstValue(vehicle.engine) ?? '').toLowerCase();
                const colourValue = String(getFirstValue(vehicle.colour) ?? '').toLowerCase();
                const genericColour = String(vehicle.genericolour ?? '').toLowerCase();
                const description = String(vehicle.description ?? '').toLowerCase();
                const comments = String(vehicle.Comments ?? '').toLowerCase();
                const specification = String(vehicle.specification ?? '').toLowerCase();

                // Search within attributes array
                let attributesString = '';
                if (Array.isArray(vehicle.attributes)) {
                  attributesString = vehicle.attributes
                    .map(attr => `${attr.Name || ''} ${attr.Value || ''}`)
                    .join(' ')
                    .toLowerCase();
                }

                const year = getYearAsString(vehicle.year);

                // Combine searchable fields
                const searchableText = [
                  vehicleTitle, make, model, badge, year, condition, bodyType,
                  transmissionValue, fuelValue, engineValue,
                  colourValue, genericColour,
                  description, attributesString, comments, specification
                ].join(' ');

                // Split search term into individual words for better multi-word matching
                const searchWords = searchTerm.split(/\s+/);

                // Check if AT LEAST ONE search word is included in the searchable text
                const someWordsMatch = searchWords.some(word => searchableText.includes(word));

                if (!someWordsMatch) {
                  return false;
                }
              }
          }

          return true; // Vehicle passes all active filters
        });
        return filteredVehicles; // Return the array
      } catch (e) {
        console.error(`[Getter currentlyFilteredVehicles] Error during filtering:`, e);
        return []; // Return empty array on error
      }
    },

    // --- REVISED Dynamic Filter Option Getters (with correct counts) ---

    dynamicMakes(): FilterOption[] {
      // Check for empty vehicle set early to avoid calculations
      const vehiclesStore = useVehiclesStore();
      if (!Array.isArray(vehiclesStore.vehicles) || vehiclesStore.vehicles.length === 0) {
        return [];
      }

      // Get vehicles filtered by all OTHER filters (excluding Make, Model)
      const vehiclesFilteredByOtherFacets = _getVehiclesFilteredBy(
        this.frontPageFilters,
        vehiclesStore.vehicles,
        vehiclesStore,
        ['make', 'model']
      );

      // 2. Count occurrences of each make within this NEW filtered list
      const makeCounts = new Map<string, { displayValue: string; count: number }>();
      vehiclesFilteredByOtherFacets.forEach(vehicle => { 
          const value = getFirstValue(vehicle.make);
          const displayValue = vehicle.make?.displayValue?.[0] || value;
          if (value && displayValue) {
              if (!makeCounts.has(value)) {
                  makeCounts.set(value, { displayValue: displayValue, count: 0 });
              }
              makeCounts.get(value)!.count++; 
          }
      });

      // 3. Create the FilterOption array from the counts derived from vehiclesFilteredByOtherFacets
      let optionsWithCounts: FilterOption[] = Array.from(makeCounts.entries()).map(([value, data]) => ({
          value: value,
          displayValue: data.displayValue,
          count: data.count
      }));

      // 4. Ensure all *globally available* makes are present in the list.
      // If a make wasn't in vehiclesFilteredByOtherFacets, its count will be 0.
      // Also, ensure any currently *selected* make is in the list, even if its count became 0 due to other filters.
      const presentValues = new Set(optionsWithCounts.map(opt => opt.value));
      
      vehiclesStore.availableFilters.make.forEach(availableMakeValue => {
          if (!presentValues.has(availableMakeValue)) {
              const makeDataFromAllVehicles = vehiclesStore.vehicles.find(v => getFirstValue(v.make) === availableMakeValue)?.make;
              const displayValue = makeDataFromAllVehicles?.displayValue?.[0] || availableMakeValue;
              optionsWithCounts.push({
                  value: availableMakeValue,
                  displayValue: displayValue,
                  count: 0 // Its count is 0 because it wasn't in vehiclesFilteredByOtherFacets
              });
          }
      });
      
      // If a make is actively selected in frontPageFilters, ensure its count reflects reality against vehiclesFilteredByOtherFacets.
      // This might be redundant if the initial loop over vehiclesFilteredByOtherFacets was correct, but acts as a safeguard.
      if (this.frontPageFilters.make.length > 0) {
          this.frontPageFilters.make.forEach(selectedMakeValue => {
              const existingOption = optionsWithCounts.find(opt => opt.value === selectedMakeValue);
              const countInFiltered = vehiclesFilteredByOtherFacets.filter(v => getFirstValue(v.make) === selectedMakeValue).length;
              if (existingOption) {
                  existingOption.count = countInFiltered; // Correct the count based on vehiclesFilteredByOtherFacets
              } else {
                  // This case should ideally be covered by the loop over vehiclesStore.availableFilters.make
                  // but if not, add it with the correct count.
                  const makeDataFromAllVehicles = vehiclesStore.vehicles.find(v => getFirstValue(v.make) === selectedMakeValue)?.make;
                  const displayValue = makeDataFromAllVehicles?.displayValue?.[0] || selectedMakeValue;
                  optionsWithCounts.push({
                      value: selectedMakeValue,
                      displayValue: displayValue,
                      count: countInFiltered
                  });
              }
          });
      }

      return optionsWithCounts.sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    },

    dynamicModels(): FilterOption[] {
      // Check for empty vehicle set early
      const vehiclesStore = useVehiclesStore();
      if (!Array.isArray(vehiclesStore.vehicles) || vehiclesStore.vehicles.length === 0) {
        return [];
      }

      // Get vehicles filtered by applicable filters but EXCLUDING model
      const vehiclesFilteredExcludingModel = _getVehiclesFilteredBy(
        this.frontPageFilters,
        vehiclesStore.vehicles,
        vehiclesStore,
        ['model']
      );

      // 2. Count occurrences of each model within this filtered list
      const modelCounts = new Map<string, { displayValue: string; displayMake?: string; count: number }>();
      vehiclesFilteredExcludingModel.forEach(vehicle => {
          const modelValue = getFirstValue(vehicle.model); // Use the sanitized value (e.g., 'camry', not 'toyota_camry')
          const displayValue = vehicle.model?.displayValue?.[0] || modelValue; // Display name (e.g., 'Camry')
          const displayMake = vehicle.make?.displayValue?.[0]; // Make name (e.g., 'Toyota')
          
          // Combine make and model for the filter VALUE used elsewhere
          // Corrected logic: Assume modelValue might already contain make, extract only model code if needed.
          const modelCodeOnly = modelValue?.includes('_') ? modelValue.split('_').slice(1).join('_') : modelValue;
          const makeValue = getFirstValue(vehicle.make);
          const filterValue = makeValue && modelCodeOnly ? `${makeValue}_${modelCodeOnly}` : '';

          if (filterValue && modelValue && displayValue) { // Ensure we have key identifiers
              if (!modelCounts.has(filterValue)) {
                  modelCounts.set(filterValue, { displayValue: displayValue, displayMake: displayMake, count: 0 });
              }
              modelCounts.get(filterValue)!.count++
          }
      });

      // 3. Create the FilterOption array
      const optionsWithCounts: FilterOption[] = Array.from(modelCounts.entries()).map(([value, data]) => ({
          value: value, // Use the combined 'make_model' value for consistency
          displayValue: data.displayValue,
          displayMake: data.displayMake,
          count: data.count
      }));
      
      // 4. Add back selected models with zero count if they aren't present
      this.frontPageFilters.model.forEach(selectedModelValue => { // selectedModelValue is 'make_model'
          if (!modelCounts.has(selectedModelValue)) {
              // Find the original vehicle data to extract display names
              const matchingVehicle = vehiclesStore.vehicles.find(v => {
                   const makeVal = getFirstValue(v.make);
                   const modelVal = getFirstValue(v.model);
                   return makeVal && modelVal && `${makeVal}_${modelVal}` === selectedModelValue;
              });
              const displayValue = matchingVehicle?.model?.displayValue?.[0] || selectedModelValue.split('_')[1] || selectedModelValue; // Fallback
              const displayMake = matchingVehicle?.make?.displayValue?.[0] || selectedModelValue.split('_')[0]; // Fallback
              
              optionsWithCounts.push({
                  value: selectedModelValue,
                  displayValue: displayValue,
                  displayMake: displayMake,
                  count: 0
              });
          }
      });

      // 5. Sort by make, then model
      return optionsWithCounts.sort((a, b) => {
          const makeCompare = (a.displayMake || '').localeCompare(b.displayMake || '');
          if (makeCompare !== 0) return makeCompare;
          return a.displayValue.localeCompare(b.displayValue);
      });
    },

    dynamicBodyTypes(): FilterOption[] {
      // Check for empty vehicle set early to avoid calculations
      const vehiclesStore = useVehiclesStore();
      if (!Array.isArray(vehiclesStore.vehicles) || vehiclesStore.vehicles.length === 0) {
        return [];
      }

      // Get vehicles filtered by all OTHER filters (excluding Body)
      const vehiclesFilteredExcludingBody = _getVehiclesFilteredBy(
        this.frontPageFilters,
        vehiclesStore.vehicles,
        vehiclesStore,
        ['body']
      );

      // 2. Count occurrences of each body type within this filtered list
      const bodyCounts = new Map<string, { displayValue: string; count: number }>();
      vehiclesFilteredExcludingBody.forEach(vehicle => {
          const value = getFirstValue(vehicle.body);
          const displayValue = vehicle.body?.displayValue?.[0] || value;
          if (value && displayValue) {
              if (!bodyCounts.has(value)) {
                  bodyCounts.set(value, { displayValue: displayValue, count: 0 });
              }
              bodyCounts.get(value)!.count++
          }
      });

      // 3. Create the FilterOption array
      let optionsWithCounts: FilterOption[] = Array.from(bodyCounts.entries()).map(([value, data]) => ({
          value: value,
          displayValue: data.displayValue,
          count: data.count
      }));
      
      // 4. Ensure all globally available body types are present & selected ones have correct counts
      const presentValues = new Set(optionsWithCounts.map(opt => opt.value));
      vehiclesStore.availableFilters.body.forEach(availableBodyValue => {
          if (!presentValues.has(availableBodyValue)) {
              const bodyDataFromAllVehicles = vehiclesStore.vehicles.find(v => getFirstValue(v.body) === availableBodyValue)?.body;
              const displayValue = bodyDataFromAllVehicles?.displayValue?.[0] || availableBodyValue;
              optionsWithCounts.push({
                  value: availableBodyValue,
                  displayValue: displayValue,
                  count: 0 
              });
          }
      });

      if (this.frontPageFilters.body.length > 0) {
          this.frontPageFilters.body.forEach(selectedBodyValue => {
              const existingOption = optionsWithCounts.find(opt => opt.value === selectedBodyValue);
              const countInFiltered = vehiclesFilteredExcludingBody.filter(v => getFirstValue(v.body) === selectedBodyValue).length;
              if (existingOption) {
                  existingOption.count = countInFiltered;
              } else {
                  const bodyDataFromAllVehicles = vehiclesStore.vehicles.find(v => getFirstValue(v.body) === selectedBodyValue)?.body;
                  const displayValue = bodyDataFromAllVehicles?.displayValue?.[0] || selectedBodyValue;
                  optionsWithCounts.push({
                      value: selectedBodyValue,
                      displayValue: displayValue,
                      count: countInFiltered
                  });
              }
          });
      }

      return optionsWithCounts.sort((a, b) => a.displayValue.localeCompare(b.displayValue));
    },

    dynamicConditions(): FilterOption[] {
      // Check for empty vehicle set early to avoid calculations
      const vehiclesStore = useVehiclesStore();
      if (!Array.isArray(vehiclesStore.vehicles) || vehiclesStore.vehicles.length === 0) {
        return [];
      }

      // Get a subset of vehicles filtered ONLY by keyword and budget (for global counts)
      // Exclude condition, make, model, and body from filtering to get baseline vehicle set
      const vehiclesForConditionCounts = _getVehiclesFilteredBy(
        this.frontPageFilters,
        vehiclesStore.vehicles,
        vehiclesStore,
        ['condition', 'make', 'model', 'body']
      );

      // 2. Count occurrences of each condition within THIS broader list
      const conditionCountsBasedOnRelevantFilters = new Map<string, { displayValue: string; count: number }>();
      vehiclesForConditionCounts.forEach(vehicle => {
          const value = getFirstValue(vehicle.condition);
          const displayValue = vehicle.condition?.displayValue?.[0] || value;
          if (value && displayValue) {
              if (!conditionCountsBasedOnRelevantFilters.has(value)) {
                  conditionCountsBasedOnRelevantFilters.set(value, { displayValue: displayValue, count: 0 });
              }
              conditionCountsBasedOnRelevantFilters.get(value)!.count++;
          }
      });

      // 3. Create the FilterOption array using these broader counts
      //    AND ensure all globally available conditions are present.
      let optionsWithCounts: FilterOption[] = [];
      const globallyAvailableConditions = vehiclesStore.availableFilters.condition || []; // Ensure this is an array

      globallyAvailableConditions.forEach(availableConditionValue => {
          const foundData = conditionCountsBasedOnRelevantFilters.get(availableConditionValue);
          let displayValue: string;
          let count: number;

          if (foundData) {
              displayValue = foundData.displayValue;
              count = foundData.count;
          } else {
              // If not found in the 'vehiclesForConditionCounts', its count is 0 for the current keyword/budget,
              // but we still need its display name from the global vehicle list.
              const conditionDataFromAllVehicles = vehiclesStore.vehicles.find(v => getFirstValue(v.condition) === availableConditionValue)?.condition;
              displayValue = conditionDataFromAllVehicles?.displayValue?.[0] || availableConditionValue;
              count = 0; // Count is 0 relative to current keyword/budget if not in vehiclesForConditionCounts
          }
          optionsWithCounts.push({
              value: availableConditionValue,
              displayValue: displayValue,
              count: count
          });
      });
      
      // The selection state itself is handled by `isFilterOptionSelected` in the component.

      const conditionOrder = { 'new': 1, 'demo': 2, 'used': 3 };
      return optionsWithCounts.sort((a, b) => {
        const orderA = conditionOrder[a.value as keyof typeof conditionOrder] || 99;
        const orderB = conditionOrder[b.value as keyof typeof conditionOrder] || 99;
        return orderA - orderB;
      });
    },

    // --- End Dynamic Filter Option Getters ---

    // Check if any filters are active
    hasNoActiveFilters(): boolean {
      const vehiclesStore = useVehiclesStore();
      try {
        const result = (
          !this.frontPageFilters.search_keywords?.trim() &&
          this.frontPageFilters.make.length === 0 &&
          this.frontPageFilters.model.length === 0 &&
          this.frontPageFilters.body.length === 0 &&
          this.frontPageFilters.condition.length === 0 &&
          (!this.frontPageFilters.perweek ||
            (() => {
              const priceRangeFilter = vehiclesStore.availableFilters.priceRange;
              const defaultMin = priceRangeFilter?.min ?? 0;
              const defaultMax = priceRangeFilter?.max ?? 100000;
              const defaultPerweekMin = calculateWeeklyPayment(defaultMin);
              const defaultPerweekMax = calculateWeeklyPayment(defaultMax);
              return this.frontPageFilters.perweek.min === defaultPerweekMin &&
                     this.frontPageFilters.perweek.max === defaultPerweekMax;
            })())
        );
        return result;
      } catch (e) {
        throw e; // Re-throw error so navSearchFilteredCount can catch it
      }
    },

    // Get current filter query for navigation
    currentFilterQuery(): Record<string, string> {
      const params: Record<string, string> = {};
      
      // Add search query first to ensure it's included
      if (this.frontPageFilters.search_keywords) {
        params.search_keywords = this.frontPageFilters.search_keywords;
      }
      
      // Body Types
      if (this.frontPageFilters.body.length > 0) {
        params.body = this.frontPageFilters.body.join(',');
      }

      // Make
      if (this.frontPageFilters.make.length > 0) {
        params.make = this.frontPageFilters.make.join(',');
      }

      // Model
      if (this.frontPageFilters.model.length > 0) {
        params.model = this.frontPageFilters.model.join(',');
      }

      // Badge
      if (this.frontPageFilters.badge.length > 0) {
        // Extract just the badge value (after the colon) and join with commas
        const simplifiedBadges = this.frontPageFilters.badge.map(badge => {
          // Badge format is "modelValue:badgeValue", extract just the badgeValue
          const parts = badge.split(':');
          const badgeValue = parts.length > 1 ? parts[1] : badge;
          // No need to URL encode here - this happens automatically when converting to query string
          // Special chars like '+' will be properly encoded by the browser
          return badgeValue;
        });
        params.badge = simplifiedBadges.join(',');
      }
      
      // Condition
      if (this.frontPageFilters.condition.length > 0) {
        params.condition = this.frontPageFilters.condition.join(',');
      }

      // Add perweek filter ONLY if it's not at default values
      if (this.frontPageFilters.perweek) {
        // **MODIFIED: Compare against stored default range**
        // const priceRangeFilter = useVehiclesStore().availableFilters.priceRange; // No longer needed here
        // const defaultMin = priceRangeFilter?.min ?? 0;
        // const defaultMax = priceRangeFilter?.max ?? 100000;
        // const defaultPerweekMin = calculateWeeklyPayment(defaultMin);
        // const defaultPerweekMax = calculateWeeklyPayment(defaultMax);
        let isDefault = this.frontPageFilters.perweek.min === this.defaultPerWeekRange.min &&
                        this.frontPageFilters.perweek.max === this.defaultPerWeekRange.max;

        if (!isDefault) {
            params.perweek_min = String(this.frontPageFilters.perweek.min);
            params.perweek_max = String(this.frontPageFilters.perweek.max);
        }
      }

      return params;
    }
  },

  actions: {
    // Helper method to check if a vehicle matches the current filters
    vehicleMatchesFilters(vehicle: Vehicle): boolean {
      // Check make filter
      if (this.frontPageFilters.make.length > 0 && !this.frontPageFilters.make.includes(vehicle.make?.value?.[0] || '')) {
        return false
      }

      // Check model filter
      if (this.frontPageFilters.model.length > 0 && !this.frontPageFilters.model.includes(vehicle.model?.value?.[0] || '')) {
        return false
      }

      // Check body filter
      if (this.frontPageFilters.body.length > 0 && !this.frontPageFilters.body.includes(vehicle.body?.value?.[0] || '')) {
        return false
      }

      // Check condition filter
      if (this.frontPageFilters.condition.length > 0 && !this.frontPageFilters.condition.includes(vehicle.condition?.value?.[0] || '')) {
        return false
      }

      // Check price range
      if (this.frontPageFilters.perweek) {
        // First try to use the perweek value if it's already calculated
        let perweek = Number(vehicle.perweek ?? 0);

        // If perweek is not available or invalid, calculate it from price
        if (perweek <= 0 && vehicle.price) {
          let price = 0;

          if (typeof vehicle.price === 'number') {
            price = vehicle.price;
          } else if (isVehiclePriceProperty(vehicle.price)) {
            price = Number(vehicle.price.value[0]);
          }

          perweek = calculateWeeklyPayment(price);
        }

        if (perweek < this.frontPageFilters.perweek.min || perweek > this.frontPageFilters.perweek.max) {
          return false
        }
      }

      return true
    },

    // Optimize initialization to reduce redundant operations
    async initializeFrontPage() {
      if (this.loading) return false; // Prevent concurrent initialization

      try {
        this.loading = true;
        this.error = null;
        const vehiclesStore = useVehiclesStore();
        // await vehiclesStore.fetchVehicles(); // REMOVED: Data should be fetched by the page/plugin

        // Ensure data is loaded before proceeding
        if (!vehiclesStore.isDataLoaded) {
          console.warn('[frontPageStore] Vehicle data not loaded before initializing front page. Fetching required.');
          // Optionally, trigger fetch here if absolutely necessary as a fallback,
          // but ideally, the page should handle this.
           await vehiclesStore.fetchVehicles(); // Keep fetch as fallback if needed
        }

        // **NEW: Set default perweek range AFTER vehiclesStore is ready**
        const priceRangeFilter = vehiclesStore.availableFilters.priceRange;
        const defaultMinPrice = priceRangeFilter?.min ?? 0;
        const defaultMaxPrice = priceRangeFilter?.max ?? 100000; // Or a more appropriate large number
        const defaultPerweekMin = calculateWeeklyPayment(defaultMinPrice);
        const defaultPerweekMax = calculateWeeklyPayment(defaultMaxPrice);
        // **Store the calculated default AND set the initial filter value**
        this.defaultPerWeekRange = { min: defaultPerweekMin, max: defaultPerweekMax };
        this.frontPageFilters.perweek = { min: defaultPerweekMin, max: defaultPerweekMax };
        // **END NEW**

        this.calculateVehicleCounts();
        this.loadFilterOptionsInternal();
        this.loadFeaturedVehiclesInternal();
        this.loadPopularMakesAndModelsInternal();
        this.initializeFilterCountsInternal();
        this.loading = false;
        return true;
      } catch (error) {
        console.error('Error initializing front page:', error);
        this.error = error instanceof Error ? error.message : 'Failed to initialize front page';
        this.loading = false;
        return false;
      }
    },

    // Refactored action to load filter options
    loadFilterOptionsInternal() {
       const vehiclesStore = useVehiclesStore();
       if (!vehiclesStore.isDataLoaded || vehiclesStore.vehicles.length === 0) {
            console.warn('[frontPageStore] Cannot load filter options: Vehicle data not ready.');
            return;
       }
       const vehicles = vehiclesStore.vehicles; // Access .value

       // Makes (Use availableFilters from main store)
       this.filterOptions.makes = vehiclesStore.availableFilters.make.map(m => ({ value: m, displayValue: m.charAt(0).toUpperCase() + m.slice(1) })); // Basic capitalization

       // Body Types (Use availableFilters from main store)
       this.filterOptions.bodyTypes = vehiclesStore.availableFilters.body.map(b => ({ value: b, displayValue: b.charAt(0).toUpperCase() + b.slice(1) })); // Basic capitalization

       // Conditions (Extract from actual vehicles)
       const uniqueConditions = new Map<string, string>();
       vehicles.forEach(v => {
           const conditionValue = v.condition?.value?.[0];
           const conditionDisplay = v.condition?.displayValue?.[0] || conditionValue;
           if (conditionValue && conditionDisplay) {
               uniqueConditions.set(conditionValue, conditionDisplay);
           }
       });
       this.filterOptions.conditions = Array.from(uniqueConditions.entries()).map(([value, displayValue]) => ({ value, displayValue }));

       // Models (Extract from actual vehicles, simple list for now)
       // For grouped models like in MakeModelBadgeFilter, more complex logic is needed here.
       const uniqueModels = new Map<string, { display: string; make?: string }>();
       vehicles.forEach(v => {
           const modelValue = v.model?.value?.[0]; // e.g., tucson
           const modelDisplay = v.model?.displayValue?.[0]; // e.g., Tucson
           const makeDisplay = v.make?.displayValue?.[0]; // e.g., Hyundai
           if (modelValue && modelDisplay) {
                // Use make_model format for value? Or keep simple?
                // Let's use the simple model value for now, assuming FacetedSearchWidget handles it.
               if (!uniqueModels.has(modelValue)) {
                   uniqueModels.set(modelValue, { display: modelDisplay, make: makeDisplay });
               }
           }
       });
        this.filterOptions.models = Array.from(uniqueModels.entries()).map(([value, data]) => ({
            value,
            displayValue: data.display,
            displayMake: data.make // Add make info if needed by UI
        }));

       // console.log('[frontPageStore] Filter options loaded.', this.filterOptions);
       // Removed the previous warning
    },
    loadFeaturedVehiclesInternal() {
        const vehiclesStore = useVehiclesStore();
        this.featuredVehicles = vehiclesStore.vehicles
            .filter((v: Vehicle) => Array.isArray(v.photos) && v.photos.length > 0)
            .sort((a: Vehicle, b: Vehicle) => {
                 const yearA = Number(getYearAsString(a.year)) || 0;
                 const yearB = Number(getYearAsString(b.year)) || 0;
                return yearB - yearA;
            })
            .slice(0, 6);
    },
    loadPopularMakesAndModelsInternal() {
         const vehiclesStore = useVehiclesStore();
         const makeCounts: Record<string, number> = {};
         const modelCounts: Record<string, number> = {};
         vehiclesStore.vehicles.forEach((vehicle: Vehicle) => {
            const make = getFirstValue(vehicle.make);
            const model = getFirstValue(vehicle.model);
            if (make) makeCounts[make] = (makeCounts[make] || 0) + 1;
            if (model) modelCounts[model] = (modelCounts[model] || 0) + 1;
         });
         const topMakes = Object.entries(makeCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([make]) => make);
         const topModels = Object.entries(modelCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([model]) => model);
         this.popularMakes = this.filterOptions.makes.filter((option: FilterOption) => topMakes.includes(option.value)).sort((a: FilterOption, b: FilterOption) => (makeCounts[b.value] || 0) - (makeCounts[a.value] || 0));
         this.popularModels = this.filterOptions.models.filter((option: FilterOption) => topModels.includes(option.value)).sort((a: FilterOption, b: FilterOption) => (modelCounts[b.value] || 0) - (modelCounts[a.value] || 0));
    },
    initializeFilterCountsInternal() {
      const vehiclesStore = useVehiclesStore();
      const vehicles = vehiclesStore.vehicles;

      const counts = {
        make: {} as Record<string, number>,
        model: {} as Record<string, number>,
        body: {} as Record<string, number>,
        condition: {} as Record<string, number>
      };

      vehicles.forEach(vehicle => {
        const makeValue = vehicle.make?.value?.[0];
        const modelValue = vehicle.model?.value?.[0];
        const bodyValue = vehicle.body?.value?.[0];
        const conditionValue = vehicle.condition?.value?.[0];

        if (makeValue) counts.make[makeValue] = (counts.make[makeValue] || 0) + 1;
        if (modelValue) counts.model[modelValue] = (counts.model[modelValue] || 0) + 1;
        if (bodyValue) counts.body[bodyValue] = (counts.body[bodyValue] || 0) + 1;
        if (conditionValue) counts.condition[conditionValue] = (counts.condition[conditionValue] || 0) + 1;
      });

      this.filterCounts = counts;

      this.vehicleCounts = {
        total: vehicles.length,
        new: vehicles.filter(v => v.condition?.value?.[0]?.toLowerCase() === 'new').length,
        demo: vehicles.filter(v => v.condition?.value?.[0]?.toLowerCase() === 'demo').length,
        used: vehicles.filter(v => v.condition?.value?.[0]?.toLowerCase() === 'used').length
      };
    },
    calculateVehicleCounts() {
      const vehiclesStore = useVehiclesStore();
      const vehicles = vehiclesStore.vehicles;

      this.vehicleCounts.total = vehicles.length;

      this.vehicleCounts.new = vehicles.filter(v =>
        v.condition?.value?.[0]?.toLowerCase() === 'new'
      ).length;

      this.vehicleCounts.demo = vehicles.filter(v =>
        v.condition?.value?.[0]?.toLowerCase() === 'demo'
      ).length;

      this.vehicleCounts.used = vehicles.filter(v =>
        v.condition?.value?.[0]?.toLowerCase() === 'used'
      ).length;
    },
    updateFilterCounts() {
      // Remove or adapt
    },
    updateFilter(filterName: string, value: any) {
      // Validate filter name is a valid key in our frontPageFilters
      if (!isValidFilterKey(filterName)) return;

      // Cast to the validated key type
      const key = filterName as keyof FrontPageFilters;

      // Directly modify the state for search_keywords
      if (key === 'search_keywords') {
        this.frontPageFilters.search_keywords = String(value || '');
      }
      // Keep object replacement for range filter
      else if (key === 'perweek') {
        if (typeof value === 'object' && value !== null && 'min' in value && 'max' in value &&
            typeof value.min === 'number' && typeof value.max === 'number') {
          // Still replace the object for range to ensure reactivity on min/max change
          this.frontPageFilters = { ...this.frontPageFilters, perweek: { min: value.min, max: value.max } };
        } else {
          console.warn(`[frontPageStore] Received unexpected value structure for perweek filter:`, value);
        }
      }
      // Directly modify array filters
      else if (['make', 'model', 'body', 'condition', 'badge'].includes(key)) {
        let primitiveValue: string[];
        // console.log(`[updateFilter - ${key}] Received value:`, JSON.stringify(value)); // Log received value
        // Extract primitive values as before
        if (typeof value === 'string') {
          primitiveValue = [value];
        } else if (Array.isArray(value)) {
          primitiveValue = value.filter((item: unknown): item is string => typeof item === 'string');
        } else {
          console.warn(`[frontPageStore] Unexpected value type for ${key}:`, value);
          primitiveValue = [];
        }

        // Ensure primitiveValue is an array
        if (!Array.isArray(primitiveValue)) {
          console.warn(`[frontPageStore] Expected array for ${key}:`, primitiveValue);
          primitiveValue = [primitiveValue];
        }
        this.frontPageFilters[key] = primitiveValue;
      }

      // console.log(`[updateFilter] frontPageFilters AFTER update:`, JSON.stringify(this.frontPageFilters)); // Keep commented out unless debugging
    },

    // Clear a specific filter
    clearFilter(filterName: string) {
      if (!isValidFilterKey(filterName)) return;

      if (filterName === 'search_keywords') {
        this.frontPageFilters.search_keywords = '';
      } else if (filterName === 'perweek') {
        const vehiclesStore = useVehiclesStore();
        const priceRangeFilter = vehiclesStore.availableFilters.priceRange;
        const defaultMin = priceRangeFilter?.min ?? 0;
        const defaultMax = priceRangeFilter?.max ?? 100000;
        this.frontPageFilters.perweek = {
            min: calculateWeeklyPayment(defaultMin),
            max: calculateWeeklyPayment(defaultMax)
        };
      } else if (['make', 'model', 'body', 'condition', 'badge'].includes(filterName)) {
        (this.frontPageFilters[filterName] as string[]) = [];
      }
    },

    // Clear all filters
    clearAllFilters() {
      // Reset all filter values to their defaults
      this.frontPageFilters = {
        condition: [],
        make: [],
        model: [],
        body: [],
        badge: [],
        perweek: this.defaultPerWeekRange, // Use default range
        search_keywords: ''
      };

      // ... existing code ...
    },

    // Update budget filter specifically
    updateBudgetFilter(budgetRange: { min: number; max: number }) {
      this.updateFilter('perweek', budgetRange);
    },

    // Apply front page filters to vehicles store
    async applyFiltersToVehiclesStore() {
      const vehiclesStore = useVehiclesStore();
      vehiclesStore.applyFilters({ ...this.frontPageFilters });
      return true;
    }
  }
});